import { useAuthStore } from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    DateTime,
    Money,
    ObjectRef,
    PageHeader,
    StatusBadge,
    type ListColumnDef,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { StaffRefund } from '@/api/types';
import { REFUND_SOURCE_WORDS, RefundSheet } from '@/components/refund-sheet';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { P } from '@/lib/permissions';
import { orderHref } from '@/pages/orders/index';

const PATH = '/staff/refunds';
const PAGE_KEY = 'refunds';

/** The route's prefetch: the person's views, then the rows of the view they land on (BM-46 §2.0 + §2.4). */
export const refundsPrefetch = adminListPrefetch(PAGE_KEY, PATH);

const refundFilters = filterDefs(PATH, {
    q: 'Search',
    state: 'State',
    source: 'Source',
    charge_id: 'Charge',
    order_id: 'Order',
    range: 'Created',
});

/** The issuer by name and role; an id that names no user or key shows as written. */
export function Issuer({ refund }: { refund: StaffRefund }) {
    if (refund.initiated_by_name === null)
        return <span className="font-mono text-xs">{refund.initiated_by}</span>;
    return (
        <span className="flex flex-col">
            <span>{refund.initiated_by_name}</span>
            {refund.initiated_by_role ? (
                <span className="text-muted-foreground text-xs">
                    {refund.initiated_by_role}
                </span>
            ) : null}
        </span>
    );
}

export function RefundsPage() {
    const can = useAuthStore((s) => s.can);
    const [params] = useSearchParams();
    const [open, setOpen] = useState(params.has('charge'));
    const columns: ListColumnDef<StaffRefund>[] = [
        {
            key: 'id',
            label: 'Refund',
            cell: (r) => <span className="font-mono text-xs">{r.id}</span>,
            csv: (r) => r.id,
            boardCard: true,
            locked: 'start',
            noTruncate: true,
        },
        {
            key: 'charge_id',
            aliases: ['charge'],
            label: 'Charge',
            csv: (r) => r.charge_id,
            cell: (r) => <ObjectRef id={r.charge_id} />,
        },
        {
            key: 'order_id',
            aliases: ['order'],
            label: 'Order',
            csv: (r) => r.order_id ?? '',
            cell: (r) =>
                r.order_id ? (
                    <ObjectRef id={r.order_id} href={orderHref(r.order_id)} />
                ) : (
                    '—'
                ),
        },
        {
            key: 'merchant_id',
            aliases: ['merchant'],
            label: 'Merchant',
            sortKey: 'merchant_id',
            csv: (r) => r.merchant_id,
            cell: (r) => <ObjectRef id={r.merchant_id} />,
        },
        {
            key: 'amount_cents',
            aliases: ['amount'],
            label: 'Amount',
            sortKey: 'amount_cents',
            csv: (r) => r.amount_cents,
            boardCard: true,
            cell: (r) => <Money cents={r.amount_cents} currency={r.currency} />,
            noTruncate: true,
        },
        {
            key: 'state',
            label: 'State',
            sortKey: 'state',
            cell: (r) => <StatusBadge status={r.state} domain="refund" />,
            csv: (r) => r.state,
            groupable: { key: 'state', statusDomain: 'refund' },
        },
        {
            key: 'source',
            label: 'Source',
            sortKey: 'source',
            csv: (r) => REFUND_SOURCE_WORDS[r.source],
            cell: (r) => REFUND_SOURCE_WORDS[r.source],
        },
        {
            key: 'initiated_by',
            label: 'Initiated by',
            csv: (r) => r.initiated_by_name ?? r.initiated_by,
            cell: (r) => <Issuer refund={r} />,
        },
        {
            key: 'decided_by',
            label: 'Approved by',
            csv: (r) => r.decided_by_name ?? r.decided_by ?? '',
            cell: (r) =>
                r.decided_by === null
                    ? '—'
                    : (r.decided_by_name ?? r.decided_by),
        },
        {
            key: 'reason',
            label: 'Reason',
            cell: (r) => r.reason,
            csv: (r) => r.reason,
        },
        {
            key: 'created_at',
            aliases: ['created'],
            label: 'Issued',
            kind: 'datetime',
            sortKey: 'created_at',
            csv: (r) => r.created_at,
            cell: (r) => <DateTime value={r.created_at} empty="—" />,
            noTruncate: true,
        },
    ];
    const { state, query } = useAdminList<StaffRefund>(PAGE_KEY, PATH, {
        columns,
        filters: refundFilters,
    });
    return (
        <div>
            <PageHeader
                title="Refunds"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                "Refunds issued by staff on a merchant's behalf. Above the merchant's limit the refund waits in the approvals queue."
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
                primaryAction={
                    can(P.refundsIssue) ? (
                        <Button onClick={() => setOpen(true)}>
                            Issue a refund
                        </Button>
                    ) : null
                }
            />
            <AdminListPage
                noun="refunds"
                state={state}
                query={query}
                rowKey={(r) => r.id}
                controls={{
                    q: { kind: 'search' },
                    state: { domain: 'refund' },
                    charge_id: { kind: 'text', label: 'Charge id' },
                    order_id: { kind: 'text', label: 'Order id' },
                }}
            />
            {open ? (
                <RefundSheet
                    initialChargeId={params.get('charge') ?? ''}
                    onClose={() => setOpen(false)}
                />
            ) : null}
        </div>
    );
}
